"use client";

import { type FormEvent, type KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";

import { PROMO_COOLDOWN_DAYS, STORAGE_KEYS } from "../constants";
import { showToast } from "../toast";

type HintState = "ok" | "error" | "info";
type Hint = { text: string; state: HintState };

type PromoCheckResponse = {
  status: "valid" | "not_found" | "inactive" | "not_started" | "expired" | "exhausted";
  grantMonths: number | null;
  grantsSignupAccess: boolean;
};

function cookieDecided(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEYS.cookieConsent) !== null;
  } catch {
    return false;
  }
}

function promoRecentlyClosed(): boolean {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.promoSeen);
    if (!raw) return false;
    const seen = new Date(raw).getTime();
    if (Number.isNaN(seen)) return false;
    return Date.now() - seen < PROMO_COOLDOWN_DAYS * 86_400_000;
  } catch {
    return false;
  }
}

function markPromoSeen(): void {
  try {
    window.localStorage.setItem(STORAGE_KEYS.promoSeen, new Date().toISOString());
  } catch {
    /* noop */
  }
}

function getFocusables(root: HTMLElement): HTMLElement[] {
  const selector =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => !el.hasAttribute("hidden") && el.getAttribute("aria-hidden") !== "true"
  );
}

/** Тексты состояний: попап показывает то, что ответил сервер, а не «успех» из воздуха. */
function hintForResponse(response: PromoCheckResponse, code: string): Hint {
  switch (response.status) {
    case "valid":
      return {
        text: `Код действителен: ${response.grantMonths ?? 3} мес. платного тарифа бесплатно. Введите его при регистрации.`,
        state: "ok",
      };
    case "exhausted":
      return { text: "Этот код уже использован. Проверьте написание или спросите новый у того, кто его выдал.", state: "error" };
    case "expired":
      return { text: "Срок действия кода истёк.", state: "error" };
    case "inactive":
      return { text: "Код отключён.", state: "error" };
    case "not_started":
      return { text: "Код ещё не активен.", state: "error" };
    default:
      return {
        text: `Код «${code}» не найден. Проверьте написание.`,
        state: "error",
      };
  }
}

export function PromoPopup() {
  const [open, setOpen] = useState(false);
  const [hint, setHint] = useState<Hint | null>(null);
  const [checking, setChecking] = useState(false);
  const [verifiedCode, setVerifiedCode] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);
  const triggered = useRef(false);
  const timerDone = useRef(false);
  const scrollDone = useRef(false);

  const openPromo = useCallback((auto: boolean) => {
    if (auto) {
      if (triggered.current || promoRecentlyClosed() || !cookieDecided()) return;
      triggered.current = true;
    }
    lastFocused.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setHint(null);
    setVerifiedCode(null);
    setOpen(true);
  }, []);

  const closePromo = useCallback(() => {
    setOpen(false);
    document.body.classList.remove("no-scroll");
    markPromoSeen();
    lastFocused.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    document.body.classList.add("no-scroll");
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const onDocumentClick = (event: Event) => {
      if (!(event.target instanceof Element) || !event.target.closest("[data-promo-open]")) return;
      event.preventDefault();
      openPromo(false);
    };
    document.addEventListener("click", onDocumentClick);
    return () => document.removeEventListener("click", onDocumentClick);
  }, [openPromo]);

  useEffect(() => {
    const tryAutoOpen = () => {
      if (timerDone.current && scrollDone.current) openPromo(true);
    };

    const timer = setTimeout(() => {
      timerDone.current = true;
      tryAutoOpen();
    }, 18_000);

    let ticking = false;
    const onScroll = () => {
      if (ticking || scrollDone.current) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const doc = document.documentElement;
        const scrollable = doc.scrollHeight - window.innerHeight;
        if (scrollable > 0 && window.scrollY / scrollable >= 0.5) {
          scrollDone.current = true;
          tryAutoOpen();
        }
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    let onMouseOut: ((event: MouseEvent) => void) | null = null;
    if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      onMouseOut = (event: MouseEvent) => {
        if (triggered.current || promoRecentlyClosed() || !cookieDecided()) return;
        if (event.relatedTarget) return;
        if (event.clientY > 0) return;
        openPromo(true);
      };
      document.addEventListener("mouseout", onMouseOut);
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      if (onMouseOut) document.removeEventListener("mouseout", onMouseOut);
    };
  }, [openPromo]);

  const onDialogKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Escape") {
        closePromo();
        return;
      }
      if (event.key !== "Tab") return;
      const root = dialogRef.current;
      if (!root) return;
      const list = getFocusables(root);
      if (list.length === 0) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [closePromo]
  );

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = (inputRef.current?.value ?? "").trim().toUpperCase();
    if (!value) {
      setHint({ text: "Введите промокод или начните бесплатный доступ.", state: "info" });
      inputRef.current?.focus();
      return;
    }

    setChecking(true);
    try {
      const response = await fetch("/api/promo/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: value }),
      });

      if (!response.ok) {
        setHint({ text: "Не удалось проверить код. Попробуйте позже.", state: "error" });
        return;
      }

      const result = (await response.json()) as PromoCheckResponse;
      setHint(hintForResponse(result, value));
      if (result.status === "valid") {
        setVerifiedCode(value);
        showToast("Промокод действителен.");
      } else {
        setVerifiedCode(null);
      }
    } catch {
      setHint({ text: "Не удалось проверить код. Проверьте соединение.", state: "error" });
    } finally {
      setChecking(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal" id="promo-modal" data-promo-modal>
      <button
        type="button"
        className="modal__overlay"
        aria-label="Закрыть окно"
        data-promo-close
        onClick={closePromo}
      />
      <div
        ref={dialogRef}
        className="modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="promo-title"
        aria-describedby="promo-desc"
        onKeyDown={onDialogKeyDown}>
        <button
          type="button"
          className="modal__close"
          data-promo-close
          aria-label="Закрыть окно"
          onClick={closePromo}>
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
            <path
              d="M6 6l12 12M18 6 6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <p className="eyebrow">Промокод</p>
        <h2 className="modal__title" id="promo-title">
          3 месяца бесплатно
        </h2>
        <p className="modal__text" id="promo-desc">
          Забирайте 3 месяца платного тарифа без оплаты. Промокод выдаётся мастерам — введите его при
          регистрации, и период начнётся сразу.
        </p>

        <form className="promo-form" data-promo-form noValidate onSubmit={onSubmit}>
          <label className="field">
            <span className="field__label">Промокод</span>
            <input
              ref={inputRef}
              className="field__input"
              type="text"
              name="promo"
              placeholder="Например, SLOT-AQEY-N4M4"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              data-promo-input
            />
          </label>
          <p className="promo-form__hint" data-promo-hint data-state={hint?.state} role="status" aria-live="polite">
            {hint?.text ?? ""}
          </p>
          <button type="submit" className="btn btn--primary btn--block" disabled={checking}>
            {checking ? "Проверяем…" : "Применить промокод"}
          </button>
        </form>

        {verifiedCode ? (
          <a
            className="modal__link"
            href={`/signup?inviteCode=${encodeURIComponent(verifiedCode)}`}
            data-promo-close
            onClick={closePromo}>
            Перейти к регистрации с этим кодом
          </a>
        ) : (
          <a className="modal__link" href="/signup" data-promo-close onClick={closePromo}>
            Хочу сначала попробовать — начать с 30 дней бесплатно
          </a>
        )}
      </div>
    </div>
  );
}
